import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Order, OrderService } from 'us-web-services';
import { getLongDateString } from 'us-react-components/dist/utils/date';
import { faListUl } from '@fortawesome/pro-solid-svg-icons/faListUl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, ButtonSize, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const CARD_CLASSES = 'bg-white border border-neutral-200 rounded p-4 mb-3';

type TypeCode = 'booking' | 'membership';

interface TxType {
  code: TypeCode;
  labelKey: string;
}

const SUPPORT_EMAIL = 'support@urbansitter.com';

export const Transactions = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: _pathId } = useParams<{ id: string }>();
  void _pathId;
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;
  const userId = user?.id;

  const types = useMemo<TxType[]>(
    () => [
      { code: 'booking', labelKey: 'pages.transactions.types.booking' },
      { code: 'membership', labelKey: 'pages.transactions.types.membership' },
    ],
    [],
  );

  const initialTypeCode = ((): TypeCode => {
    const requestedType = searchParams.get('type') as TypeCode | null;
    if (requestedType && types.some((option) => option.code === requestedType)) {
      return requestedType;
    }
    return 'booking';
  })();

  const [selectedType, setSelectedType] = useState<TxType>(
    types.find((option) => option.code === initialTypeCode) ?? types[0],
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [transactions, setTransactions] = useState<Order[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(true);

  useEffect(() => {
    document.title = 'UrbanSitter - Transactions';
  }, []);

  const loadPage = async (nextPage: number, reset = false): Promise<void> => {
    if (!userId) return;
    setLoaded(false);
    const params: Record<string, unknown> = {
      page: nextPage,
      sort: 'createdAt',
      'createdAt.dir': 'desc',
      statuses: 'success,deposit_failed_hard',
    };

    if (selectedType.code === 'booking') {
      params.reasons = ['booking'];
      params.payeeId = userId;
    } else {
      params.reasons = ['subscription_setup', 'subscription_installment'];
      params.userId = userId;
    }

    try {
      const resp = (await OrderService.getByFilter({ params })).data;
      const data = resp?.data ?? [];
      setTransactions((prev) => (reset ? data : [...prev, ...data]));
      setTotalSize(resp?.meta?.size ?? 0);
    } catch (err) {
      console.warn('[Transactions] load failed', err);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    setTransactions([]);
    setPage(1);
    loadPage(1, true);

  }, [selectedType.code, userId]);

  const onMore = (): void => {
    const next = page + 1;
    setPage(next);
    loadPage(next, false);
  };

  const hasMore = transactions.length < totalSize;

  const emptyKey =
    selectedType.code === 'booking'
      ? 'pages.transactions.empty.sitterBooking'
      : 'pages.transactions.empty.membership';

  return (
    <div className='container py-8'>
      <div className='flex flex-wrap items-center gap-3 mb-3'>
        <h1 className='text-2xl font-semibold mb-0'>{t('pages.transactions.heading')}</h1>
        {selectedType.code === 'booking' && (
          <Button
            id='childcare-transactions'
            buttonType={ButtonType.primary}
            buttonSize={ButtonSize.small}
            className='text-sm'
            onClick={(): void => {
              window.location.assign('/app/childcare-transactions');
            }}
          >
            {t('pages.transactions.exportFriendly')}
          </Button>
        )}
      </div>

      <div className='relative inline-block mb-4'>
        <Button
          buttonType={ButtonType.tertiary}
          className='flex items-center gap-2 font-semibold !text-xl !text-current'
          onClick={(): void => setMenuOpen((previous) => !previous)}
        >
          <FontAwesomeIcon icon={faListUl} className='text-base' />
          <span>{t(selectedType.labelKey)}</span>
        </Button>
        {menuOpen && (
          <ul className='absolute left-0 mt-2 bg-white border border-neutral-200 rounded shadow z-10 w-56'>
            {types.map((option) => (
              <li key={option.code}>
                <Button
                  buttonType={ButtonType.tertiary}
                  className='w-full text-left !rounded-none px-3 py-2 hover:bg-neutral-100'
                  onClick={(): void => {
                    setSelectedType(option);
                    setMenuOpen(false);
                  }}
                >
                  {t(option.labelKey)}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='max-w-3xl'>
        {transactions.map((order) => {
          if (selectedType.code === 'membership') {
            return <MembershipCard key={order.id} transaction={order} t={t} />;
          }
          return (
            <SitterBookingCard
              key={order.id}
              transaction={order}
              t={t}
              onClick={(): void => {
                if (order.itemBooking?.bookingId) {
                  navigate(`/bookings/${order.itemBooking.bookingId}`);
                }
              }}
            />
          );
        })}

        {transactions.length === 0 && loaded && (
          <h2 className='text-lg'>{t(emptyKey)}</h2>
        )}

        {hasMore && (
          <div className='text-center mt-5'>
            <Button buttonType={ButtonType.primary} onClick={onMore}>
              {loaded ? (
                t('pages.transactions.moreTransactions')
              ) : (
                <span>
                  <span
                    className='inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin align-middle mr-2'
                    aria-hidden='true'
                  />
                  {t('pages.transactions.loading')}
                </span>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

const MembershipCard = ({ transaction, t }: { transaction: Order; t: TranslateFn }): JSX.Element => {
  const created = getLongDateString(transaction.created) || t('pages.transactions.dateNotAvailable');
  const hasDiscount = Number(transaction.discount ?? 0) > 0;
  return (
    <div className={CARD_CLASSES}>
      <div className='flex gap-3'>
        <span className='icon-sm parent-member' />
        <div>
          <b>{t('pages.transactions.totalCharged', { amount: transaction.totalIn ?? 0 })}</b>
          <br />
          {created}
          {hasDiscount && (
            <>
              <div>{t('pages.transactions.fee', { amount: transaction.fee ?? 0 })}</div>
              <div>
                {t('pages.transactions.discountApplied', { amount: transaction.discount })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SitterBookingCard = ({
  transaction,
  t,
  onClick,
}: {
  transaction: Order;
  t: TranslateFn;
  onClick: () => void;
}): JSX.Element => {
  const ok = transaction.status === 'success';
  const failed = transaction.status === 'deposit_failed_hard';
  const created = getLongDateString(transaction.created) || t('pages.transactions.dateNotAvailable');
  const payoutFeeAmount = Number(transaction.itemPayoutFee?.amount ?? 0);
  const tip = Number(transaction.itemBooking?.tip ?? 0);
  const serviceTotal = Number(transaction.itemBooking?.total ?? 0);

  return (
    <div
      className={`${CARD_CLASSES} cursor-pointer`}
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event): void => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
    >
      {ok && <span className='icon-sm icon-svg-checked inline-block mr-2' />}
      {failed && (
        <div className='flex items-center text-red-600'>
          <i className='fa-solid fa-dollar-sign mr-2' />
          <b>{t('pages.transactions.payoutNotProcessed')}</b>
        </div>
      )}
      <div className='ml-8'>
        {ok && (
          <div>
            <b>{t('pages.transactions.totalDeposited', { amount: transaction.totalOut ?? 0 })}</b>
          </div>
        )}
        <div>{created}</div>
        {serviceTotal > 0 && (
          <div>
            {t('pages.transactions.serviceTotal', { amount: serviceTotal })}
          </div>
        )}
        {tip > 0 && (
          <div>
            {t('pages.transactions.tip', { amount: tip })}
          </div>
        )}
        {payoutFeeAmount > 0 && (
          <div>
            {t('pages.transactions.instantPayoutFee', { amount: payoutFeeAmount })}
          </div>
        )}
      </div>
      {failed && (
        <div>
          <Button
            id='go-mailto'
            buttonType={ButtonType.secondary}
            onClick={(event): void => {
              event.stopPropagation();
              window.location.href = `mailto:${SUPPORT_EMAIL}`;
            }}
            className='mt-3'
            style={{ width: 200 }}
          >
            {t('pages.transactions.contactSupport')}
          </Button>
        </div>
      )}
    </div>
  );
};
